import { computed, onBeforeUnmount, ref } from 'vue';
import { apiClient } from '@/Clients/ApiClient';
import { useApiFetch } from '@/Composables/useApiFetch';
import { usePagination } from '@/Composables/usePagination';
import ProjectSearchService from '@/Services/ProjectSearchService';
import { debounce } from '@/Utils';

export function useSearchFormResults() {
    const service = new ProjectSearchService(apiClient);
    const searchRequest = useApiFetch();
    const pagination = usePagination(5);
    const searchTerm = ref('');
    const isDebouncing = ref(false);
    const hasCompletedSearch = ref(false);

    const isProcessing = computed(() => isDebouncing.value || searchRequest.isProcessing.value);
    const isLoading = computed(() => isProcessing.value && !hasCompletedSearch.value);
    const isLoadingMore = computed(() => searchRequest.isProcessing.value && hasCompletedSearch.value);
    const hasResults = computed(() => pagination.totalItemCount.value > 0);
    const isEmpty = computed(() => hasCompletedSearch.value && !hasResults.value && !isProcessing.value);
    const canLoadMore = computed(() => pagination.hasMorePagedItems.value && !isProcessing.value && !searchRequest.hasError.value);

    const load = (pageIndex) => {
        isDebouncing.value = false;
        return searchRequest.execute(
            signal => service.searchProjects(
                { term: searchTerm.value }, pagination.pageSize.value, pageIndex, { signal },
            ),
            {
                resetDataOnStart: false,
                onSuccess: ({ data }) => {
                    pagination.setPaginationData(data.data, pageIndex === 0);
                    hasCompletedSearch.value = true;
                },
            },
        );
    };

    const debouncedLoad = debounce(() => load(0));

    const reset = () => {
        debouncedLoad.cancel();
        searchRequest.reset();
        pagination.resetPaginationData();
        searchTerm.value = '';
        isDebouncing.value = false;
        hasCompletedSearch.value = false;
    };

    const search = (term) => {
        reset();
        searchTerm.value = term.trim();
        if (searchTerm.value.length < 2) {
            return;
        }

        isDebouncing.value = true;
        debouncedLoad();
    };

    const loadMore = () => {
        if (canLoadMore.value) {
            return load(pagination.pageIndex.value + 1);
        }
    };

    onBeforeUnmount(reset);

    return {
        pagedItems: pagination.pagedItems,
        totalItemCount: pagination.totalItemCount,
        pageSize: pagination.pageSize,
        error: searchRequest.error,
        isLoading,
        isLoadingMore,
        canLoadMore,
        hasResults,
        isEmpty,
        search,
        loadMore,
        reset,
    };
}
